import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import BoyIcon from '@mui/icons-material/Face';
import GirlIcon from '@mui/icons-material/Face3';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ConfirmationDialog from '../components/ConfirmationDialog';
import Loading from '../components/Loading';
import StudentRatingEditorDialog, {
  StudentRatingEditorDialogProps,
} from '../components/Students/StudentDetail/StudentRatingEditDialog';
import SubjectSelector from '../components/SubjectSelector';
import {
  getClassFromFirstYear,
  getCurrentAcademicYear,
  getCurrentYearSegment,
} from '../helpers/academic-years';
import { Paths, toStudentDetail } from '../routes/paths';
import categoriesService from '../services/categories-service';
import ratingsService from '../services/ratings-service';
import studentRatingsService from '../services/student-ratings-service';
import studentsService from '../services/students-service';
import subjectsService from '../services/subjects-service';
import { YearSegment } from '../types/category';
import { CategoryWithRatings } from '../types/category-with-ratings';
import { Gender, Student } from '../types/student';
import { StudentRating } from '../types/student-rating';
import { Subject } from '../types/subject';

const StudentDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentAcademicYear = getCurrentAcademicYear();
  const currentYearSegment = getCurrentYearSegment();
  const params = useParams<{ studentId: string }>();
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [loadingRating, setLoadingRating] = useState(true);
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [student, setStudent] = useState<Student>(location.state);
  const [prevStudent, setPrevStudent] = useState<Student | undefined>();
  const [nextStudent, setNextStudent] = useState<Student | undefined>();
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>();
  const [showDeleteRatingDialog, setShowDeleteRatingDialog] = useState(false);
  const [currentRating, setCurrentRating] = useState<StudentRating | undefined>(
    undefined
  );
  const [ratingEditDialogProps, setRatingEditDialogProps] =
    useState<StudentRatingEditorDialogProps | null>(null);
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState<number>(currentAcademicYear);
  const [selectedYearSegment, setSelectedYearSegment] =
    useState<YearSegment>(currentYearSegment);

  const fetchCurrentRating = () => {
    if (!student && !params.studentId) {
      console.warn('No student ID provided');
      return navigate(Paths.Students);
    }

    if (!selectedSubjectId) return;

    setLoadingRating(true);

    studentRatingsService
      .getByStudentIdAndSubjectIdAndYear(
        student?.id?.toString() ?? params.studentId!,
        selectedSubjectId,
        selectedAcademicYear,
        selectedYearSegment
      )
      .then(setCurrentRating)
      .catch((error) => {
        console.error('Error while fetching student rating:', error);
        setCurrentRating(undefined);
      })
      .finally(() => setLoadingRating(false));
  };

  useEffect(() => {
    if (!student && !params.studentId) {
      console.warn('No student ID provided');
      return navigate(Paths.Students);
    }

    if (location.state && student !== location.state) {
      setStudent(location.state);
    }

    Promise.all([
      student ?? studentsService.get(params.studentId!),
      subjectsService.getAll(),
    ])
      .then(([student, subjects]) => {
        if (!student) {
          console.warn(`Student with ID ${params.studentId!} not found`);
          navigate(Paths.Students);
        } else {
          setStudent(student);
        }

        studentsService
          .getNext(student)
          .then((nextStudent) => setNextStudent(nextStudent));
        studentsService
          .getPrev(student)
          .then((prevStudent) => setPrevStudent(prevStudent));

        setSelectedSubjectId(subjects[0].id!.toString());
        setAllSubjects(subjects);
      })
      .catch((error) => {
        console.error('Error while fetching student detail data:', error);
        navigate(Paths.Students);
      })
      .finally(() => setLoadingStudent(false));
  }, [
    navigate,
    student,
    params.studentId,
    location.state,
    currentAcademicYear,
  ]);

  useEffect(() => {
    setFilteredSubjects(
      allSubjects.filter(
        (subject) =>
          !subject.years ||
          subject.years.includes(selectedAcademicYear - student.firstYear + 1)
      )
    );
  }, [selectedAcademicYear, allSubjects, student.firstYear]);

  useEffect(fetchCurrentRating, [
    selectedSubjectId,
    selectedAcademicYear,
    selectedYearSegment,
    student,
    navigate,
    params.studentId,
  ]);

  const handleCreateRating = async (
    textRating: string,
    selectedCategoryIDs: string[]
  ) => {
    const rating = getRatingFromNewTextAndSelectedRatingIds(
      textRating,
      selectedCategoryIDs
    );
    await studentRatingsService.add(rating);

    setLoadingRating(true);
    fetchCurrentRating();
    setRatingEditDialogProps(null);
  };

  const handleEditRating = (
    textRating: string,
    selectedCategoryIDs: string[]
  ) => {
    const rating = {
      id: currentRating!.id,
      ...getRatingFromNewTextAndSelectedRatingIds(
        textRating,
        selectedCategoryIDs
      ),
    };
    studentRatingsService.update(rating);
    setRatingEditDialogProps(null);
    setCurrentRating(rating);
  };

  const handleDeleteRating = () => {
    studentRatingsService.remove(currentRating!.id!);
    setShowDeleteRatingDialog(false);
    setCurrentRating(undefined);
  };

  const getRatingFromNewTextAndSelectedRatingIds = (
    newTextRating: string,
    newSelectedRatingIds: string[]
  ): StudentRating => ({
    studentId: student.id!.toString(),
    subjectId: selectedSubjectId!,
    year: selectedAcademicYear,
    yearSegment: selectedYearSegment,
    text: newTextRating,
    selectedRatingIds: newSelectedRatingIds,
  });

  const getSelectedSubject = () =>
    filteredSubjects.find((subject) => subject.id === selectedSubjectId);

  const onEditRating = async () => {
    const categoriesWithRatings = await getCategoriesWithRatings();
    setRatingEditDialogProps({
      categoriesWithRatings,
      onClose: () => setRatingEditDialogProps(null),
      onConfirm: currentRating ? handleEditRating : handleCreateRating,
      student,
      subjectName: getSelectedSubject()!.name,
      selectedRatingIds: currentRating?.selectedRatingIds ?? [],
      textRating: currentRating?.text,
    });
  };

  const onExportRatings = async () => {
    const ratings: StudentRating[] = [];

    for (const subject of filteredSubjects) {
      const rating =
        currentRating?.subjectId === subject.id
          ? currentRating
          : await studentRatingsService.getByStudentIdAndSubjectIdAndYear(
              student?.id?.toString() ?? params.studentId!,
              subject.id!.toString(),
              selectedAcademicYear,
              selectedYearSegment
            );

      if (rating) ratings.push(rating);
    }

    const subjectRatings = ratings.map((rating) => rating.text);
    const text = subjectRatings.join('\n\n');

    const classNumber = getClassFromFirstYear(student.firstYear);
    const fileName = `${currentAcademicYear}-${
      currentAcademicYear + 1
    } ${selectedYearSegment.valueOf()}. pol. ${classNumber}. tř. - ${
      student.name
    }.txt`;

    const element = document.createElement('a');
    element.setAttribute(
      'href',
      `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`
    );
    element.setAttribute('download', fileName);
    element.style.display = 'none';

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const onPrevStudent = () => {
    if (!prevStudent?.id) return;

    setStudent(prevStudent);
    navigate(toStudentDetail(prevStudent.id.toString()), {
      state: prevStudent,
    });
  };

  const onNextStudent = () => {
    if (!nextStudent?.id) return;

    setStudent(nextStudent);
    navigate(toStudentDetail(nextStudent.id.toString()), {
      state: nextStudent,
    });
  };

  const getUniversalCategoriesWithRatings = async (): Promise<
    CategoryWithRatings[]
  > => {
    const categories = await categoriesService.getUniversal();
    const ratings = await ratingsService.getByCategoryIds(
      categories.map((category) => category.id!.toString())
    );

    return categories
      .map((category) => ({
        category,
        ratings: ratings.filter((rating) => rating.categoryId === category.id),
      }))
      .filter((categoryWithRatings) => categoryWithRatings.ratings.length > 0);
  };

  const getCategoriesWithRatings = async (): Promise<CategoryWithRatings[]> => {
    const year = getClassFromFirstYear(student.firstYear);
    const categories =
      await categoriesService.getBySubjectIdAndYearAndYearSegment(
        selectedSubjectId!,
        year,
        selectedYearSegment
      );

    const ratings = await ratingsService.getByCategoryIds(
      categories.map((category) => category.id!.toString())
    );

    const currentCategoriesWithRatings = categories
      .map((category) => ({
        category,
        ratings: ratings.filter((rating) => rating.categoryId === category.id),
      }))
      .filter((categoryWithRatings) => categoryWithRatings.ratings.length > 0);

    return [
      ...(await getUniversalCategoriesWithRatings()),
      ...currentCategoriesWithRatings,
    ];
  };

  const getClassTextFromFirstYear = () => {
    const year = student.firstYear;
    const classNumber = getClassFromFirstYear(year);

    return classNumber <= 9
      ? `${classNumber}. třída`
      : `Absolvent (akademický rok ${year + 8}/${year + 9})`;
  };

  const academicYears = useMemo(() => {
    const years = Array.from(
      { length: currentAcademicYear - student.firstYear + 1 },
      (_, index) => currentAcademicYear - index
    );
    if (selectedAcademicYear - student.firstYear < 0) {
      setSelectedAcademicYear(currentAcademicYear);
    }

    return years;
  }, [currentAcademicYear, student, selectedAcademicYear]);

  const isCurrentYearSegmentSelected = useMemo(
    () =>
      selectedAcademicYear === currentAcademicYear &&
      selectedYearSegment === currentYearSegment,
    [
      selectedAcademicYear,
      selectedYearSegment,
      currentAcademicYear,
      currentYearSegment,
    ]
  );

  if (loadingStudent) return <Loading />;

  return (
    <>
      {showDeleteRatingDialog && (
        <ConfirmationDialog
          onClose={() => setShowDeleteRatingDialog(false)}
          onConfirm={handleDeleteRating}
          title={`Smazat hodnocení žáka pro předmět ${
            getSelectedSubject()?.name
          }?`}
          description={'Tato akce je nevratná!'}
          confirmText="Smazat"
        />
      )}
      {ratingEditDialogProps && (
        <StudentRatingEditorDialog {...ratingEditDialogProps} />
      )}
      <Box display="flex" justifyContent={'space-between'} mb={2}>
        <Button variant="contained" onClick={() => navigate(Paths.Students)}>
          Zpět
        </Button>
        <Box display="flex" gap={1}>
          <Tooltip title="Předchozí žák" arrow>
            <span>
              <IconButton disabled={!prevStudent} onClick={onPrevStudent}>
                <ArrowBackIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Následující žák" arrow>
            <span>
              <IconButton disabled={!nextStudent} onClick={onNextStudent}>
                <ArrowForwardIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box
        display="flex"
        alignItems={'center'}
        justifyContent="space-between"
        flexWrap={'wrap'}
        gap={2}
      >
        <Box display="flex" gap={2} alignItems={'center'}>
          <Avatar sx={{ width: '120px', height: '120px' }}>
            {student.gender === Gender.Male ? (
              <BoyIcon sx={{ fontSize: '100px' }} />
            ) : (
              <GirlIcon sx={{ fontSize: '80px' }} />
            )}
          </Avatar>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="h4">{student.name}</Typography>
            <Typography variant="h6">{getClassTextFromFirstYear()}</Typography>
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" gap={2} alignItems="center">
            <InputLabel id="academicYear">Akademický rok</InputLabel>
            <Select
              required
              labelId="academicYear"
              variant="outlined"
              size="small"
              value={selectedAcademicYear}
              onChange={(event) =>
                setSelectedAcademicYear(event.target.value as number)
              }
            >
              {academicYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {`${year}/${year + 1}`}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <ToggleButtonGroup
            orientation="horizontal"
            value={selectedYearSegment}
            exclusive
            onChange={(_, yearSegment) => {
              if (yearSegment === null) return;
              return setSelectedYearSegment(yearSegment);
            }}
            size="small"
            sx={{ justifyContent: 'right' }}
          >
            <ToggleButton value={YearSegment.FirstHalf}>
              <Typography noWrap>1. pololetí</Typography>
            </ToggleButton>
            <ToggleButton value={YearSegment.SecondHalf}>
              <Typography noWrap>2. pololetí</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <SubjectSelector
        subjects={filteredSubjects}
        selectedSubjectId={selectedSubjectId ?? allSubjects[0].id!.toString()}
        onSelectedSubjectIdChange={setSelectedSubjectId}
      />
      {loadingRating ? (
        <Loading />
      ) : (
        <>
          {currentRating?.text ? (
            <Typography textAlign={'justify'} whiteSpace={'pre-wrap'}>
              {currentRating.text}
            </Typography>
          ) : (
            <Typography variant="h6">{'Bez hodnocení'}</Typography>
          )}
          <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
            <>
              {isCurrentYearSegmentSelected && (
                <>
                  {currentRating && (
                    <Button onClick={() => setShowDeleteRatingDialog(true)}>
                      {'Smazat'}
                    </Button>
                  )}
                  <Button variant="contained" onClick={onEditRating}>
                    {currentRating ? 'Upravit' : 'Vytvořit'}
                  </Button>
                  <Button variant="contained" onClick={onExportRatings}>
                    {'Exportovat'}
                  </Button>
                </>
              )}
            </>
          </Box>
        </>
      )}
    </>
  );
};

export default StudentDetail;
